import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck } from "lucide-react";
import Reveal from "@/components/layout/Reveal";

export const FeaturedProductSection = () => {
  return (
    <section data-testid="featured-product" className="py-20 sm:py-28 bg-[#0B132B] text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-[0.06] pointer-events-none" />
      <div className="absolute -top-40 -left-40 h-[500px] w-[500px] rounded-full bg-[#10B981]/20 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-[#10B981]/8 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 sm:px-8 relative grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-6 order-2 lg:order-1">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#10B981]/20 text-[#10B981] text-[11px] font-semibold tracking-widest uppercase">
            <span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" /> Featured product
          </span>
          <h2 className="font-heading mt-5 text-3xl sm:text-5xl lg:text-[56px] font-bold tracking-tight leading-[1.05]">
            NeoPower <span className="text-[#10B981]">Black Shield</span> Series.
          </h2>
          <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
            The next-generation premium hot water heat pump from AeroMax Energy — engineered for efficiency, quiet operation and modern Kiwi homes.
          </p>

          <div className="mt-7 flex flex-wrap gap-2.5">
            {[
              { t: "COP up to 5.4", sub: "Industry-leading efficiency" },
              { t: "DC Full Inverter Technology", sub: "Adaptive, quieter performance" },
            ].map((h, i) => (
              <span
                key={i}
                data-testid={`product-highlight-${i}`}
                className="inline-flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/8 backdrop-blur border border-white/15"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#10B981] text-white shrink-0">
                  <BadgeCheck className="h-4 w-4" strokeWidth={2.5} />
                </span>
                <span>
                  <p className="font-heading font-semibold text-white text-sm leading-tight">{h.t}</p>
                  <p className="text-[11px] text-white/55 mt-0.5">{h.sub}</p>
                </span>
              </span>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
            {[
              { v: "5.4", l: "COP up to" },
              { v: "80%", l: "Save up to" },
              { v: "42 dBa", l: "Silent mode" },
              { v: "7 yr", l: "Tank warranty*" },
            ].map((s, i) => (
              <div key={i} className="rounded-2xl bg-white/5 border border-white/10 p-5">
                <p className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">{s.l}</p>
                <p className="font-heading text-3xl font-bold mt-1 num-tab text-white">{s.v}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/neopower-black-shield"
              data-testid="featured-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-7 py-4 font-semibold transition-all hover:shadow-xl hover:shadow-emerald-500/30 active:scale-95"
            >
              Explore Black Shield <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/savings-calculator"
              className="inline-flex items-center gap-2 rounded-full border-2 border-white/30 text-white hover:bg-white hover:text-[#0B132B] px-7 py-4 font-semibold transition-all"
            >
              Calculate Savings
            </Link>
          </div>
        </Reveal>

        <Reveal delay={120} className="lg:col-span-6 order-1 lg:order-2">
          <div className="relative">
            {/* Soft green ambient glow behind the frame */}
            <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-[#10B981]/25 via-[#10B981]/5 to-transparent blur-3xl pointer-events-none" />
            {/* Premium lifestyle frame — clean, no overlay clutter */}
            <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-white/10 shadow-[0_30px_80px_-25px_rgba(0,0,0,0.65)]">
              <img
                src="/products/neopower_lifestyle.png"
                alt="NeoPower Black Shield installed in a lush Kiwi backyard"
                className="w-full h-[520px] sm:h-[620px] object-cover"
                loading="lazy"
              />
              {/* Subtle bottom gradient with product name only */}
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/70 via-black/25 to-transparent">
                <p className="font-heading text-lg sm:text-xl font-bold leading-tight text-white">NeoPower Black Shield · 270L</p>
                <p className="text-[11px] text-white/70 mt-0.5">Outdoor-rated · R290 natural refrigerant</p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
